import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';

import { trackChartExport } from '../../../util/mixpanel';
import { escapeHTML } from '../../../util/text';

let highchartUtils = require('../../../util/highcharts')(Highcharts);

let bottomXAxisConfig = {
  labels: {
    style: {
      fontSize: '12px'
    }
  },
  gridLineColor: '#F5F5F5',
  gridLineWidth: 1,
  gridZIndex: 1,
  categories: []
};
let topXAxisConfig = {
  opposite: true,
  linkedTo: 0,
  labels: {
    style: {
      fontSize: '12px',
      fontWeight: 'bold'
    }
  },
  gridLineColor: '#F5F5F5',
  gridLineWidth: 1,
  categories: []
};

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 80,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: [
    bottomXAxisConfig,
    topXAxisConfig
  ],
  yAxis: {
    min: 0,
    allowDecimals: false,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    gridZIndex: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    scatter: {
      marker: {
        lineColor: '#ececec',
        lineWidth: 1,
        lineZIndex: 2,
        symbol: 'circle',
        radius: 7,
        states: {
          hover: {
            lineColor: 'rgb(100,100,100)'
          }
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: []
};

export class CTAChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.zones = [];
    this.onRedraw = this.onRedraw.bind(this);
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  calculatePercentiles = (data, index) => {
    const lines = [],
      ticks = [],
      serie = {
        name: 'percentiles',
        type: 'scatter',
        marker: {
          enabled: false
        },
        data: [
          { x: 0, y: null },
          { x: index - 1, y: null }
        ]
      };

    let lastValue = 0;
    let maxValue = 0;
    if (!!data.series) {
      const last = _(data.series)
        .flatMap('data')
        .orderBy('y')
        .last();
      maxValue = !!last ? last.y : 0;
    }

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      const value = Math.ceil(moment.duration(pObj.rawValue, 'minutes').as(this.props.timeUnit));

      const diff = lastValue - value;
      let labelY = -4,
        tickY = 6;
      if (diff > 0 && diff < maxValue * 0.02) {
        tickY = 12;
        labelY = 12;
      }

      serie.data.push({ x: index - 1, y: value });
      lines.push({
        color: '#444444',
        dashStyle: 'dot',
        width: 2,
        zIndex: 2,
        value,
        label: {
          align: 'right',
          text,
          x: 9,
          y: 5,
          useHTML: true,
          style: {
            ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode)
          },
        }
      });

      if (_.findIndex(ticks, { value }) === -1) {
        ticks.push({
          color: '#444444',
          dashStyle: 'dot',
          width: 2,
          zIndex: 2,
          value,
          label: {
            textAlign: 'right',
            x: -9,
            y: tickY,
            text: value,
            useHTML: true,
            style: {
              ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
            }
          }
        });
        ticks.push({
          dashStyle: 'line',
          color: 'transparent',
          width: 4,
          zIndex: 2,
          value,
          events: highchartUtils.plotlinesTooltip(text)
        });
      }

      lastValue = value;
    }

    const newLines = [];
    _(lines)
      .groupBy('value')
      .forEach(l => {
        const line = l[0];
        const labels = _.map(l, i => {
          return i.label.text;
        });
        line.label.text = labels.join(', ');
        newLines.push(line);
      });

    return { lines: newLines.concat(ticks), serie };
  };

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    let that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(this, that);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;
    chartConfig.chart.events.redraw = this.onRedraw;

    chartConfig.exporting.menuItemDefinitions = {
      'downloadPNG': {
        text: 'Download PNG image',
        onclick: this.exportChart('image/png')
      },
      'downloadJPEG': {
        text: 'Download JPEG image',
        onclick: this.exportChart('image/jpeg')
      },
      'downloadPDF': {
        text: 'Download PDF image',
        onclick: this.exportChart('application/pdf')
      }
    };

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    if (this.props.data.series) {
      this.updateChartData();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.data.series && prevProps.data != this.props.data) {
      this.updateChartData(prevProps.data);
    }

    if (this.props.data.percentiles && prevProps.percentiles != this.props.percentiles) {
      this.setPercentiles();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  setPercentiles() {
    let percentiles = this.calculatePercentiles(this.props.data, this.props.data.categories.length);
    this.chart.update({
      yAxis: {
        plotLines: percentiles.lines
      }
    });
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('cta', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  updateChartData(oldData = {}) {
    let percentiles = this.calculatePercentiles(this.props.data, this.props.data.categories.length);
    this.chart.update(
      {
        yAxis: {
          plotLines: percentiles.lines,
          title: {
            text: `Cycle time (${this.props.timeUnit})`
          }
        },
        xAxis: [
          {
            ...bottomXAxisConfig,
            categories: this.props.data.categories
          },
          {
            ...topXAxisConfig,
            categories: this.props.data.cardsPerCategory
          }          
        ]
      },
      false
    );

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.chart);

    //Render percentiles
    const percentilesIndex = _.findIndex(this.chart.series, {
      name: 'percentiles'
    });
    if (percentilesIndex > -1) {
      this.chart.series[percentilesIndex].remove(false);
    }
    this.chart.addSeries(percentiles.serie, false);
    this.chart.redraw();

  }

  tooltipFormatter(chartScope, componentScope) {
    if (chartScope.key == 'Plotline') {
      return `${chartScope.point.text}: ${chartScope.y} ${componentScope.props.timeUnit}s`;
    }

    let duration = `${chartScope.y} ${componentScope.props.timeUnit}${chartScope.y != 1 ? 's' : ''}`; //moment.duration(chartScope.y, 'day').format('d [days]');
    let tooltip = `<p class="tooltip-label-size" style="margin: 8px 8px 0;">Cycle time: ${duration}</p>`;

    for (let s = 0; s < componentScope.chart.series.length; s++) {
      for (let i = 0; i < componentScope.chart.series[s].data.length; i++) {
        let point = componentScope.chart.series[s].data[i];
        if (point.card && point.x == chartScope.point.x && point.y === chartScope.y) {
          const border = !point.card.isBlocked && !!point.card.blockedTime ? ' border: 2px solid #ec2132;' : '';
          const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
          tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${border} ${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    tooltip += `<p class="tooltip-info-txt">Click on the dot to see the details</p>`;

    return tooltip;
  }

  onPointClick(comp, ev) {
    const points = [];
    const duration = `${ev.y} ${comp.props.timeUnit}${ev.y != 1 ? 's' : ''}`;

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (
          point.card &&
          point.x == ev.x &&
          moment.duration(point.y, 'day').format('d') == moment.duration(ev.y, 'day').format('d')
        ) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color,
            date: point.date
          });
        }
      }
    }

    comp.props.onPointClick({
      duration,
      points
    });
  }

  onRedraw(ev) {
    if (
      ev.target.oldChartWidth !== ev.target.chartWidth ||
      ev.target.oldChartHeight !== ev.target.chartHeight ||
      ev.target.axes[0].oldMax !== ev.target.axes[0].max ||
      ev.target.axes[0].oldMin !== ev.target.axes[0].min ||
      ev.target.axes[1].oldMax !== ev.target.axes[1].max ||
      ev.target.axes[1].oldMin !== ev.target.axes[1].min ||
      ev.target.axes[2].oldMax !== ev.target.axes[2].max ||
      ev.target.axes[2].oldMin !== ev.target.axes[2].min
    ) {
      destroyHealthZones(this.zones);
      this.zones = renderHealthZones(this.chart, this.props.data.healthZones, this.props.allHealthZones);
    }
  }

  exportChart(type) {
    return () => {
      const { healthZones } = this.props.data;
      const { allHealthZones } = this.props;
      this.chart.exportChartLocal({ type }, {
        chart: {
          events: {
            redraw: function (e) {
              renderHealthZones(this, healthZones, allHealthZones);
            },
            load: function (e) {
              e.target.options.exporting.sourceHeight = e.target.chartHeight;
              e.target.options.exporting.sourceWidth = e.target.chartWidth;
              renderHealthZones(this, healthZones, allHealthZones);
            }
          }
        }
      });
      trackChartExport('cta');
    }
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div id={`${this.props.id}-details`} className="chart_details chart_details-full"></div>
      </div>
    );
  }
}

function destroyHealthZones(zones = []) {
  while (zones.length > 0) {
    const zone = zones.pop();
    zone.destroy();
  }
}

function renderHealthZones(chart, healthZones, allHealthZones) {
  const zones = [];
  let yMax = chart.yAxis[0].max;
  let yMin = chart.yAxis[0].min;
  let width = chart.xAxis[0].toPixels(1, false) - chart.xAxis[0].toPixels(0, false);
  let height = chart.yAxis[0].toPixels(0, false) - chart.yAxis[0].toPixels(1, false);

  _.forEach(healthZones, hz => {
    const points = _.sortBy(hz, '-y');

    let w, h, p, offsetX, offsetY, next;

    const z = [];

    for (let i = 0; i < points.length; i++) {
      
      p = points[i];
      next = points[i + 1];

      offsetX = chart.xAxis[0].toPixels(p.x, false) - width / 2;
      w = width;
      if (offsetX + w > chart.plotLeft + chart.plotWidth) {
        w = chart.plotLeft + chart.plotWidth - offsetX;
      }
      if (offsetX < chart.plotLeft) {
        w = w + offsetX - chart.plotLeft;
        offsetX = chart.plotLeft;
      }

      if (!i && p.y < yMax) {
        h = (yMax - Math.max(yMin, p.y)) * height;
        offsetY = chart.plotTop;
        z.push({
          fill: 'rgba(255, 154, 162, 1)',
          offsetX,
          offsetY,
          w,
          h,
        });
      }
      
      h = next ? (p.y - next.y) * height : (p.y - yMin) * height;
      offsetY = chart.yAxis[0].toPixels(p.y, false);
      if (offsetY + h > chart.plotTop + chart.plotHeight) {
        h = chart.plotTop + chart.plotHeight - offsetY;
      }
      if (offsetY < chart.plotTop) {
        h = h + offsetY - chart.plotTop;
        offsetY = chart.plotTop;
      }
      
      z.push({
        fill: _.find(allHealthZones, { value: p.percentile }).color,
        offsetX,
        offsetY,
        w,
        h,
      });
    }

    _.forEach(z, ({ w, h, offsetX, offsetY, fill }) => {
      if (w > 0 && h > 0) {
        zones
          .push(
            chart.renderer
            .rect(offsetX, offsetY, w, h, 0)
            .attr({ fill, zIndex: 0 })
            .add()
          );
      }
    });
  });

  return zones;
}

const mapStateToProps = state => {
  return {
    allHealthZones: state.filters.allHealthZones,
    dateFormat: state.filters.dateFormat,
    percentiles: state.filters.cta.selectedPercentiles,
    selectedHealthZones: state.filters.cta.selectedHealthZones,
    timeUnit: state.filters.cta.timeUnit,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps)(CTAChart));
